import React, { useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";

import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import { validateDemand, validateLeadIn, validateUgPath, validateUgPoint } from "fond/draw/validation";
import {
  DeleteButton,
  LineStringButton,
  PointButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  TopologicalButton,
  UndoButton,
} from "fond/map/Toolbar";
import { ISGLayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

const allToolConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="selectPolygon" />],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<PointButton key="drawPoint" />, <LineStringButton key="drawLine" />],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />, <TopologicalButton key="topological" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

/**
 * This component is responsible for managing the drawing of ISG Features across multiple layers.
 */
const DataPrepDraw: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);

  // Configure drawing options with layer-specific styles
  const defaultOptions: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      snapOptions: {
        snapPx: 15,
        snapVertexPriorityDistance: 0.001,
      },
      editOptions: {
        allLayers: [
          {
            ID: ISGLayerIds.demand,
            GeometryType: "Point",
            validate: validateDemand,
            getFeatureProperties: () => ({ layerId: ISGLayerIds.demand }),
          },
          {
            ID: ISGLayerIds.leadins,
            GeometryType: "LineString",
            validate: validateLeadIn,
            options: {
              // For leadins we disable the midpoints creation
              midPoints: false,
            },
            getFeatureProperties: () => ({ layerId: ISGLayerIds.leadins }),
          },
          {
            ID: ISGLayerIds.ugPoints,
            GeometryType: "Point",
            validate: validateUgPoint,
            getFeatureProperties: () => ({ layerId: ISGLayerIds.ugPoints }),
          },
          {
            ID: ISGLayerIds.ugPath,
            GeometryType: "LineString",
            validate: validateUgPath,
            getFeatureProperties: () => ({ layerId: ISGLayerIds.ugPath }),
          },
        ],
        editingLayerIds: [ISGLayerIds.demand, ISGLayerIds.leadins, ISGLayerIds.ugPoints, ISGLayerIds.ugPath],
        editingLayerId: ISGLayerIds.demand,
      },
    }),
    []
  );

  return (
    <MultiLayerEditProvider versionId={versionId} options={defaultOptions} config={allToolConfig}>
      <MultiLayerEditPanelContent />
    </MultiLayerEditProvider>
  );
};

export default DataPrepDraw;
